/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { ShoppingBag, MessageSquare, ArrowLeft, ShieldCheck, Share2, Check, AlertCircle, X } from 'lucide-react';
import { Vendor, Product } from '../types';
import { supabase } from '../lib/supabase';

interface CatalogProps {
  businessName: string;
  onBackToHome?: () => void;
}

export default function CatalogView({ businessName, onBackToHome }: CatalogProps) {
  const [vendor, setVendor]       = useState<Vendor | null>(null);
  const [products, setProducts]   = useState<Product[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  // Zoom modal state
  const [zoomedImage, setZoomedImage] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    async function fetchCatalog() {
      setLoading(true);
      setError(null);
      try {
        const { data: vendorData, error: vErr } = await supabase
          .from('vendors')
          .select('*')
          .eq('business_name', businessName.toLowerCase())
          .single();

        if (vErr || !vendorData) {
          throw new Error(`Business "${businessName}" not found on VendorPulse.`);
        }

        const { data: productsData, error: pErr } = await supabase
          .from('products')
          .select('*')
          .eq('vendor_id', vendorData.id)
          .order('created_at', { ascending: false });

        if (pErr) throw new Error('Could not fetch products');

        setVendor(vendorData);
        setProducts(productsData ?? []);
      } catch (err: any) {
        setError(err.message || 'An error occurred while loading this page.');
      } finally {
        setLoading(false);
      }
    }
    if (businessName) fetchCatalog();
  }, [businessName]);

  // Close zoom on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setZoomedImage(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const copyCatalogLink = () => {
    const fullUrl = `${window.location.origin}/${businessName}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleOrder = (product: Product) => {
    if (!vendor) return;
    let phone = vendor.whatsapp_number.replace(/[^0-9]/g, '');
    if (phone.startsWith('0')) phone = '233' + phone.substring(1);
    else if (!phone.startsWith('233') && phone.length === 9) phone = '233' + phone;
    const priceText = product.price.toLocaleString('en-GH', { minimumFractionDigits: 2 });
    const rawMessage = `Hello ${vendor.business_name}, I want to buy "${product.title}" for ${priceText} GHS. Is it still available?`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(rawMessage)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col justify-center items-center p-6">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-slate-600 font-medium text-sm">Loading catalog...</p>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col justify-center items-center p-6">
        <div className="bg-white/40 backdrop-blur-lg p-8 rounded-3xl shadow-xl border border-white/60 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100/80 rounded-full flex items-center justify-center text-red-600 mx-auto mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">Catalog Not Found</h3>
          <p className="text-slate-500 text-sm mb-6">{error || "This business doesn't have a catalog here yet."}</p>
          <button
            onClick={onBackToHome}
            className="w-full py-2.5 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 shadow-lg shadow-orange-500/20 active:scale-95 transition-all text-sm"
          >
            Create Your Own Catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent font-sans text-slate-900 pb-12">

      {/* ── Image zoom modal ───────────────────────────────────────────────── */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <button
            onClick={() => setZoomedImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={zoomedImage.url}
            alt={zoomedImage.title}
            className="max-w-full max-h-[90dvh] rounded-2xl object-contain shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/70 text-xs font-medium">
            {zoomedImage.title}
          </p>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-md border-b border-white/60 px-4 py-3.5">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <button
            onClick={onBackToHome}
            className="p-1.5 hover:bg-white/80 rounded-full transition-colors text-slate-500 focus:outline-none"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="text-center flex-1 mx-2">
            <h1 className="font-display font-bold text-base tracking-tight text-slate-900 capitalize flex items-center justify-center gap-1">
              <span>{vendor.business_name}</span>
              {vendor.subscription_status === 'premium' && (
                <ShieldCheck className="w-4 h-4 text-emerald-500 fill-emerald-100 shrink-0" title="Verified Premium" />
              )}
            </h1>
            <p className="text-[11px] text-slate-400 font-medium">✨ Tap an item to message seller</p>
          </div>

          <button
            onClick={copyCatalogLink}
            className="p-1.5 hover:bg-white/80 rounded-full transition-colors text-slate-500 focus:outline-none"
            title="Share catalog link"
          >
            {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* ── Main ──────────────────────────────────────────────────────────── */}
      <main className="max-w-xl mx-auto px-4 mt-6 relative z-10">

        {/* Banner card — ID and status removed */}
        <div className="bg-slate-900/80 backdrop-blur-lg border border-slate-700/40 text-white p-6 rounded-3xl mb-6 relative overflow-hidden shadow-xl">
          <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none translate-y-6 translate-x-6">
            <ShoppingBag className="w-48 h-48" />
          </div>
          <div className="relative z-10">
            <span className="bg-orange-500 text-white font-mono text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full">
              WhatsApp Mart
            </span>
            <h2 className="font-display text-2xl font-bold mt-2 capitalize">{vendor.business_name}</h2>
            <p className="text-slate-300 text-xs mt-1">Browse products below and tap to order via WhatsApp.</p>
          </div>
        </div>

        {/* Product count + currency label */}
        <div className="flex items-center justify-between mb-4 px-1">
          <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500">
            Products ({products.length})
          </h3>
          <span className="text-xs text-slate-400 font-mono">prices in GHS</span>
        </div>

        {/* Empty state */}
        {products.length === 0 ? (
          <div className="bg-white/40 backdrop-blur-lg rounded-3xl border border-white/60 p-10 text-center shadow-lg">
            <div className="w-12 h-12 bg-white/60 rounded-full flex items-center justify-center text-slate-400 mx-auto mb-3">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <p className="text-slate-600 text-sm font-medium">No products listed yet.</p>
            <p className="text-slate-400 text-xs mt-1">Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {products.map((item) => {
              const isSoldOut = !item.is_available || item.stock_count <= 0;
              return (
                <div
                  key={item.id}
                  className="bg-white/40 backdrop-blur-lg border border-white/60 shadow-lg rounded-2xl overflow-hidden group flex flex-col justify-between"
                >
                  {/* Image — click anywhere to zoom */}
                  <div
                    className="relative aspect-square bg-white/30 overflow-hidden shrink-0 cursor-zoom-in"
                    onClick={() => setZoomedImage({ url: item.image_url, title: item.title })}
                  >
                    <img
                      src={item.image_url}
                      alt={item.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />

                    {/* Sold out overlay */}
                    {isSoldOut && (
                      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-10">
                        <span className="text-white font-display text-xs uppercase font-extrabold tracking-widest px-3 py-1 rounded-md shadow-md border-2 border-red-500">
                          SOLD OUT
                        </span>
                      </div>
                    )}

                    {/* Low stock badge */}
                    {!isSoldOut && item.stock_count <= 3 && (
                      <div className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        {item.stock_count} Left
                      </div>
                    )}
                  </div>

                  {/* Product info */}
                  <div className="p-3 flex flex-col flex-1 justify-between">
                    <div>
                      <h4 className="font-display font-medium text-slate-900 text-xs line-clamp-2 leading-snug">
                        {item.title}
                      </h4>
                      <p className="text-sm font-display font-bold text-slate-950 mt-1">
                        ₵ {item.price.toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    <button
                      disabled={isSoldOut}
                      onClick={() => handleOrder(item)}
                      className={`w-full mt-3 flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl text-[11px] font-semibold tracking-wide transition-all ${
                        isSoldOut
                          ? 'bg-white/30 text-slate-400 cursor-not-allowed border border-white/45'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95 cursor-pointer shadow-sm'
                      }`}
                    >
                      <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                      <span>Order</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
